// Settings — manage stored API keys for directors and TTS providers.

import { useEffect, useState } from 'react';
import { ApiError, keys, userKeys, type StoredKeySummary } from '../api.js';
import { useAuth } from '../auth.js';
import { useToast } from '../components/ui/toast.js';
import { Button } from '../components/ui/button.js';
import { LoadingBlock } from '../components/ui/spinner.js';

const PROVIDERS: {
  id: string;
  label: string;
  use: 'director' | 'tts' | 'both' | 'self_hosted';
  signupUrl: string;
}[] = [
  // Directors
  { id: 'deepseek',   label: 'DeepSeek',          use: 'director', signupUrl: 'https://platform.deepseek.com/api_keys' },
  { id: 'anthropic',  label: 'Anthropic',         use: 'director', signupUrl: 'https://console.anthropic.com/settings/keys' },
  // Both (director + TTS)
  { id: 'openai',     label: 'OpenAI',            use: 'both',     signupUrl: 'https://platform.openai.com/api-keys' },
  { id: 'gemini',     label: 'Gemini',            use: 'both',     signupUrl: 'https://aistudio.google.com/apikey' },
  // Commercial TTS
  { id: 'elevenlabs', label: 'ElevenLabs',        use: 'tts',      signupUrl: 'https://elevenlabs.io/app/api-key' },
  { id: 'azure',      label: 'Azure Speech',      use: 'tts',      signupUrl: 'https://portal.azure.com/' },
  { id: 'cartesia',   label: 'Cartesia (Sonic 3.5)', use: 'tts',   signupUrl: 'https://play.cartesia.ai/keys' },
  { id: 'deepgram',   label: 'Deepgram (Aura-2)', use: 'tts',      signupUrl: 'https://console.deepgram.com/' },
  { id: 'fish',       label: 'Fish Audio (S2 Pro)', use: 'tts',    signupUrl: 'https://fish.audio/go-api/' },
  { id: 'playht',     label: 'PlayHT (discontinued — Meta shut down Dec 2025)', use: 'tts', signupUrl: 'https://murf.ai/blog/play-ai-transition-guide' },
  // Self-hosted sidecars — no key needed
  { id: 'kokoro',     label: 'Kokoro (self-hosted)',     use: 'self_hosted', signupUrl: '' },
  { id: 'chatterbox', label: 'Chatterbox (self-hosted, voice cloning)', use: 'self_hosted', signupUrl: '' },
  { id: 'orpheus',    label: 'Orpheus (self-hosted)',    use: 'self_hosted', signupUrl: '' },
  { id: 'qwen_tts',   label: 'Qwen-TTS (self-hosted)',   use: 'self_hosted', signupUrl: '' },
  { id: 'dia',        label: 'Dia (self-hosted)',        use: 'self_hosted', signupUrl: '' },
];

export function SettingsPage() {
  const { state } = useAuth();
  const toast = useToast();
  const api = state.status === 'authed' && state.kind === 'user' ? userKeys : keys;
  const [list, setList] = useState<StoredKeySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [keyValue, setKeyValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    try {
      const r = await api.list();
      setList(r.keys);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function startAdd(provider: string) {
    setAdding(provider);
    setKeyValue('');
    setSubmitError(null);
  }

  async function handleSave() {
    if (!adding || !keyValue.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await api.upsert(adding, keyValue.trim());
      toast.success(`${r.key.provider_id} key saved`, `Validated · ${r.key.masked_preview}`);
      setAdding(null);
      setKeyValue('');
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'KEY_VALIDATION_FAILED') {
          setSubmitError('The provider rejected this key. Double-check it’s the right key for the right provider.');
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError('Network error.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(provider: string) {
    if (!confirm(`Remove the ${provider} key? You’ll need to re-add it to use that provider.`)) return;
    try {
      await api.remove(provider);
      toast.success(`${provider} key removed`);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to remove.';
      setError(msg);
      toast.error('Could not remove key', msg);
    }
  }

  function findStored(providerId: string): StoredKeySummary | undefined {
    return list?.find((k) => k.provider_id === providerId);
  }

  return (
    <div className="max-w-3xl">
      <h1 className="font-serif text-3xl mb-2">Settings</h1>
      <p className="text-muted text-sm mb-8">
        Provider keys are encrypted at rest with a master key on the server. Your keys never reach
        the browser after you save them — only a masked preview.
      </p>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {list === null ? (
        <LoadingBlock label="Loading keys…" />
      ) : (
      <div className="space-y-3">
        {PROVIDERS.map((p) => {
          const stored = findStored(p.id);
          const open = adding === p.id;
          return (
            <div
              key={p.id}
              className="border border-soft rounded-lg bg-white px-5 py-4"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.label}</span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-soft/60 text-muted rounded">
                      {p.use}
                    </span>
                  </div>
                  {p.use === 'self_hosted' ? (
                    <div className="text-xs text-muted mt-1">
                      Self-hosted sidecar — no key required.
                    </div>
                  ) : stored ? (
                    <div className="text-xs text-muted mt-1 font-mono">
                      {stored.masked_preview} · validated{' '}
                      {stored.last_validated_at
                        ? new Date(stored.last_validated_at).toLocaleString()
                        : 'never'}
                    </div>
                  ) : (
                    <a
                      href={p.signupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted hover:text-accent mt-1 block"
                    >
                      Get a key →
                    </a>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {p.use === 'self_hosted' ? null : stored ? (
                    <>
                      <button
                        onClick={() => startAdd(p.id)}
                        className="text-xs text-muted hover:text-ink px-2 py-1"
                      >
                        Rotate
                      </button>
                      <button
                        onClick={() => handleRemove(p.id)}
                        className="text-xs text-muted hover:text-accent px-2 py-1"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => startAdd(p.id)}
                      className="text-xs bg-ink text-parchment px-3 py-1.5 rounded hover:bg-ink/90 transition-colors"
                    >
                      Add key
                    </button>
                  )}
                </div>
              </div>

              {open && (
                <div className="mt-4 pt-4 border-t border-soft/60 space-y-3">
                  <input
                    type="password"
                    value={keyValue}
                    onChange={(e) => setKeyValue(e.target.value)}
                    placeholder={`Paste ${p.label} key`}
                    autoFocus
                    className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60"
                  />
                  {submitError && (
                    <div className="text-xs text-accent">{submitError}</div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="accent"
                      onClick={handleSave}
                      disabled={!keyValue.trim()}
                      loading={submitting}
                      loadingText="Validating…"
                      className="text-xs px-3 py-1.5"
                    >
                      Save
                    </Button>
                    <button
                      onClick={() => { setAdding(null); setKeyValue(''); setSubmitError(null); }}
                      className="text-xs text-muted hover:text-ink px-2 py-1.5"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="text-xs text-muted/70">
                    We validate the key against the provider before saving. If it’s wrong, nothing is stored.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      <p className="text-xs text-muted/70 mt-10">
        Your master encryption key (KEK) lives on the server at{' '}
        <code className="font-mono">/etc/scriptvoice/master.key</code> in mode 600. Keys are
        AES-256-GCM encrypted at rest. Decryption only happens inside the request handler that
        calls the provider — the plaintext never reaches a log, the database, or the browser.
      </p>
    </div>
  );
}
